import { useExternalScript } from './use-external-script';

interface PortalSections {
  ACCOUNT_DETAILS: 'account_details';
  ADDRESS: 'portal_address';
  BILLING_HISTORY: 'portal_billing_history';
  PAYMENT_SOURCES: 'portal_payment_methods';
  SUBSCRIPTION_DETAILS: 'sub_details';
}

export type PortalSectionName = keyof PortalSections;

interface Portal {
  openSection: (options: {
    sectionType: PortalSections[keyof PortalSections];
    params?: {
      subscriptionId?: string;
    };
  }) => void;
}

interface Chargebee {
  init: (config: { site: string }) => void;
  getInstance: () => {
    openCheckout: (config: {
      hostedPage: () => Promise<unknown>;
      success: (hostedPageId: string) => void;
      step?: (step: string) => void;
      loaded?: () => void;
      error?: (error: unknown) => void;
      close?: () => void;
    }) => void;
    createChargebeePortal: () => Portal;
    closeAll: () => void;
    setPortalSession(setterFunction: () => Promise<unknown>): Promise<Portal>;
    setPortalCallbacks: (callbacks: { close?: () => void; paymentSourceUpdate?: () => void }) => void;
  };
  getPortalSections: () => PortalSections;
}

declare global {
  interface Window {
    Chargebee?: Chargebee;
  }
}

if (!process.env.REACT_APP_CHARGEBEE_SITE_ID) throw new Error('Missing CHARGEBEE_SITE_ID env variable');

export const useChargebeeJS = () => {
  const isLoading = useExternalScript('https://js.chargebee.com/v2/chargebee.js', {
    onLoad() {
      if (!window.Chargebee) throw new Error('Chargebee script loaded but not available');
      // biome-ignore lint/style/noNonNullAssertion: verified above
      window.Chargebee.init({ site: process.env.REACT_APP_CHARGEBEE_SITE_ID! });
    },
  });

  return isLoading;
};
